import { z } from "zod";

type PhoneValidatorOptions = {
  minLength: number;
  maxLength: number;
  startWith: string;
  msgType: "phone" | "digits";
};

type AccountNoValidatorOptions = Omit<PhoneValidatorOptions, "msgType">;

type ValidatorSchemaType = {
  username: z.ZodString;
  phone: (options: PhoneValidatorOptions) => z.ZodString;
  otp: z.ZodString;
  legalName: z.ZodString;
  firstName: z.ZodString;
  accountName: z.ZodString;
  lastName: z.ZodString;
  payment: {
    eWallet: {
      accountNo: (options: AccountNoValidatorOptions) => z.ZodString;
      accountType: z.ZodString;
      isDefault: z.ZodOptional<z.ZodBoolean>;
    };
    bank: {
      bankKey: z.ZodString;
      accountNo: z.ZodString;
    };
  };
  loginPassword: z.ZodString;
  loginPin: z.ZodString;
  registerPassword: z.ZodString;
};

export const ValidatorSchema: ValidatorSchemaType = {
  username: z
    .string()
    .min(4, "4-13 alphanumeric without special character.")
    .max(13, "4-13 alphanumeric without special character.")
    .regex(/^\w+$/, "only letters, numbers, and underscores (_) are allowed."),
  loginPassword: z.string().min(8, "at least 8 characters with letters & numbers"),
  loginPin: z.string().min(4, "PIN length is invalid"),
  registerPassword: z
    .string()
    .min(8, "at least 8 characters with letters & numbers")
    .regex(/^(?=.*[A-Za-z])(?=.*\d).+$/, "at least 8 characters with letters & numbers")
    .regex(/^\S+$/, "spaces are not allowed"),
  phone: (options: PhoneValidatorOptions) => {
    const { minLength, maxLength, startWith, msgType } = options;
    let errMsgStartWith = "";
    let errMsgDigitsOnly = "";
    const isSingleLength = minLength === maxLength;
    switch (msgType) {
      case "phone":
        errMsgStartWith = "phone number should start with {{startWith}}";

        if (isSingleLength) {
          // Display single length message, no need to display startWith
          errMsgDigitsOnly = "phone number must be {{minLength}} digits";
        } else {
          // Display multiple length message, no need to display startWith
          errMsgDigitsOnly = "phone number must be {{minLength}}-{{maxLength}} digits";
        }
        break;
      case "digits":
        if (isSingleLength) {
          // Display single length message, need to display startWith
          errMsgStartWith = "enter a {{minLength}}-digit number starting with {{startWith}}";
          errMsgDigitsOnly = "enter a {{minLength}}-digit number starting with {{startWith}}";
        } else {
          // Display multiple length message, no need to display startWith
          errMsgStartWith = "enter a {{minLength}}-{{maxLength}} digit number starting with {{startWith}}";
          errMsgDigitsOnly = "enter a {{minLength}}-{{maxLength}} digit number starting with {{startWith}}";
        }
        break;
    }

    const startWithRegex = new RegExp(`^${startWith}`);
    const digitRegex = buildDigitLengthRegexes(minLength, maxLength, startWith);

    return z.string().regex(startWithRegex, errMsgStartWith).regex(digitRegex, errMsgDigitsOnly);
  },
  otp: z.string().min(1, "otp is required. click on get code"),
  legalName: z.string().min(1, "required"),
  firstName: z
    .string({
      required_error: "required",
    })
    .min(1, "required"),
  accountName: z
    .string({
      required_error: "required",
    })
    .min(1, "required"),
  lastName: z
    .string({
      required_error: "required",
    })
    .min(1, "required"),
  payment: {
    eWallet: {
      accountNo: (options: AccountNoValidatorOptions) => {
        const { minLength, maxLength, startWith } = options;
        const startWithRegex = new RegExp(`^${startWith}`);
        const accountNoRegex = buildDigitLengthRegexes(minLength, maxLength, startWith);
        const isSingleLength = minLength === maxLength;
        let errMsg;
        if (isSingleLength) {
          errMsg = "enter a {{minLength}}-digit number starting with {{startWith}}";
        } else {
          errMsg = "enter a {{minLength}}-{{maxLength}} digit number starting with {{startWith}}";
        }
        return z
          .string({
            required_error: "required",
          })
          .min(1, "required")
          .regex(startWithRegex, errMsg)
          .regex(accountNoRegex, errMsg);
      },
      accountType: z
        .string({
          required_error: "required",
        })
        .min(1, "required"),
      isDefault: z.boolean().optional(),
    },
    bank: {
      bankKey: z
        .string({
          required_error: "required",
        })
        .min(1, "required"),
      accountNo: z
        .string({
          required_error: "required",
        })
        .min(1, "required")
        .regex(/^\d+$/, "account number must be digits only")
        .regex(/^\d{5,34}$/, "account number must be 5-34 digits"),
    },
  },
};

/**
 * Build digit length regexes
 * Return a regex that matches any of the lengths of the digits
 * @param lengths - The lengths of the digits, if undefined, it will be ignored
 * @param offset - The offset of the digits
 * @returns The regexes
 */
const buildDigitLengthRegexes = (minLength: number, maxLength: number, startWith: string): RegExp => {
  // fill out missing lengths between min and max
  const lengths = [];
  for (let i = minLength; i <= maxLength; i++) {
    lengths.push(i);
  }

  // build regexes
  const digitLengthRegexes: string[] = [];
  lengths.forEach((length) => {
    if (!!length) {
      const lengthRegex = length - startWith.length;
      digitLengthRegexes.push(`^${startWith}\\d{${lengthRegex}}$`);
    }
  });
  return new RegExp(digitLengthRegexes.join("|"));
};
